import React from "react";

import * as Yup from "yup";

import { YardiCancelDealReasonFormExtension } from "manager/pages/Marketplace/integrations/yardi/shared";

import { VERO_CANCEL_REASON_SOMETHING_ELSE } from "shared/config/veroCancelReasons";
import { usePermissions } from "shared/hooks";
import { cancelDeal } from "shared/lib/api/dealApi";

import { useUpload } from "../hooks";
import { DealSummaryWithProperty } from "../types";

import { buildCancelHeaderMeta, isCannedIdentityCancelActive } from "./copy";

export type Form = {
  sendEmail?: string;
  emailBody?: string;
  extraParams?: Record<string, unknown>;
  veroCancelReason?: string;
  veroCancelNote?: string;
};

export const useHook = (deal: DealSummaryWithProperty) => {
  const { uploadFile, attachments } = useUpload();
  const { isIdFraudRiskEnabled: cannedEmailEnabled } = usePermissions();
  const sentilinkEnabled = Boolean(deal?.requiresSentilinkIdentification);
  const identityCheckUnfinished = Boolean(deal?.identityCheckUnfinished);
  const unit = deal?.unit;
  const property = deal?.property;
  const yardiConnected = Boolean((unit || property)?.yardiConnected);

  const schema = Yup.object().shape({
    sendEmail: Yup.bool().required("Choose to send email or not"),
    emailBody: Yup.string().when("sendEmail", {
      is: true,
      then: Yup.string().required("Email body is required"),
    }),
    veroCancelReason: sentilinkEnabled
      ? Yup.string().required("Select a reason")
      : Yup.string().nullable(),
    veroCancelNote: Yup.string().when("veroCancelReason", {
      is: VERO_CANCEL_REASON_SOMETHING_ELSE,
      then: Yup.string().trim().required("Add a note"),
      otherwise: Yup.string().nullable(),
    }),
  });

  const modalExtension =
    !sentilinkEnabled && yardiConnected ? (
      <YardiCancelDealReasonFormExtension property={property} />
    ) : null;

  const dealId = deal?.id;
  const onSubmit = async (values: Form) => {
    const { sendEmail, emailBody, extraParams, veroCancelReason, veroCancelNote } =
      values;
    await cancelDeal({
      id: dealId,
      sendEmail,
      emailBody,
      attachments,
      extraParams: {
        ...(extraParams || {}),
        ...(veroCancelReason ? { veroCancelReason } : {}),
        ...(veroCancelNote &&
        !isCannedIdentityCancelActive({
          flagOn: cannedEmailEnabled,
          reason: veroCancelReason,
          bgccAlreadyRan: deal?.bgccAlreadyRan,
        })
          ? { veroCancelNote }
          : {}),
      },
    });
  };
  const title = "Cancel Application";
  const defaultSubmitLabel = "Cancel Application";
  const emailLabel = "Would you like to send a cancellation email?";
  const headerMeta = sentilinkEnabled
    ? buildCancelHeaderMeta({
        applicantsSummary: deal?.applicantsSummary,
        unitName: unit?.name,
        propertyName: property?.name,
      })
    : undefined;
  return {
    onSubmit,
    uploadFile,
    modalExtension,
    title,
    defaultSubmitLabel,
    emailLabel,
    schema,
    sentilinkEnabled,
    identityCheckUnfinished,
    yardiConnected,
    headerMeta,
  };
};
